// Stateless drag-drop + click-to-pick file input. Emits the chosen File
// to its parent; holds no selection state of its own (the wizard owns
// the File so it can replay the same bytes on commit).

import { useRef, useState } from "react";

interface FileDropzoneProps {
  onFile: (file: File) => void;
  accept?: string;
  disabled?: boolean;
  hint?: string;
}

export function FileDropzone({
  onFile,
  accept = ".csv",
  disabled = false,
  hint = "Drop a CSV here, or click to choose",
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function pick(files: FileList | null) {
    const f = files?.[0];
    if (f) onFile(f);
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(e) => {
        if (disabled) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (disabled) return;
        e.preventDefault();
        setDragging(false);
        pick(e.dataTransfer.files);
      }}
      style={{
        border: `2px dashed ${dragging ? "var(--accent)" : "var(--border)"}`,
        borderRadius: 8,
        padding: "32px 16px",
        textAlign: "center",
        color: "var(--text-dim)",
        background: dragging ? "var(--bg-subtle, rgba(255,255,255,0.03))" : "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "border-color 0.12s, background 0.12s",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        disabled={disabled}
        style={{ display: "none" }}
        onChange={(e) => pick(e.target.files)}
      />
      <div style={{ fontSize: "0.9rem" }}>{hint}</div>
    </div>
  );
}
